type EditableElement = HTMLElement & {
  dataset: HTMLElement["dataset"] & {
    paperFieldEditUrl?: string
    paperFieldEditField?: string
    paperFieldEditType?: string
    paperFieldEditPlaceholder?: string
    paperFieldEditEmpty?: string
  }
}

function mountPaperFieldEditors(): void {
  document.querySelectorAll<EditableElement>("[data-paper-field-edit], [data-title-edit]").forEach((el) => {
    const url = el.dataset.paperFieldEditUrl ?? el.dataset.titleEditUrl
    if (!url) return

    const field = el.dataset.paperFieldEditField ?? "title"
    const inputType = el.dataset.paperFieldEditType === "textarea" ? "textarea" : "input"

    el.classList.add("is-editable")
    el.setAttribute("role", "button")
    el.setAttribute("tabindex", "0")
    el.setAttribute("title", "Click to edit")

    el.addEventListener("click", () => startEditing(el, { url, field, inputType }))
    el.addEventListener("keydown", (event) => {
      if (el.querySelector("input, textarea")) return
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        startEditing(el, { url, field, inputType })
      }
    })
  })
}

type EditorOptions = {
  url: string
  field: string
  inputType: "input" | "textarea"
}

function startEditing(el: EditableElement, options: EditorOptions): void {
  if (el.querySelector("input, textarea")) return

  const emptyText = el.dataset.paperFieldEditEmpty ?? ""
  const currentText = (el.textContent ?? "").trim()
  const current = currentText === emptyText ? "" : currentText
  const control = buildControl(el, options, current)

  el.textContent = ""
  el.appendChild(control)
  el.classList.add("is-editing")
  control.focus()
  control.select()

  let settled = false

  const finish = async (commit: boolean): Promise<void> => {
    if (settled) return
    settled = true

    const next = control.value.trim()
    if (!commit || next === current) {
      restore(el, current)
      return
    }

    if (options.field === "title" && next === "") {
      restore(el, current)
      return
    }

    const saved = await save(options.url, options.field, next)
    restore(el, saved ?? current)
  }

  control.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && options.inputType === "input") {
      event.preventDefault()
      void finish(true)
    } else if (event.key === "Escape") {
      event.preventDefault()
      settled = true
      restore(el, current)
    }
  })

  control.addEventListener("blur", () => {
    void finish(true)
  })
}

function buildControl(el: EditableElement, options: EditorOptions, current: string): HTMLInputElement | HTMLTextAreaElement {
  const control = options.inputType === "textarea"
    ? document.createElement("textarea")
    : document.createElement("input")

  if (control instanceof HTMLInputElement) {
    control.type = "text"
  } else {
    control.rows = 5
  }

  control.className = options.field === "title" ? "papers__title-input" : "papers__abstract-input"
  control.value = current
  control.setAttribute("aria-label", options.field === "title" ? "Paper title" : "Paper abstract")

  const placeholder = el.dataset.paperFieldEditPlaceholder
  if (placeholder) control.setAttribute("placeholder", placeholder)

  return control
}

function restore(el: EditableElement, value: string): void {
  const emptyText = el.dataset.paperFieldEditEmpty ?? ""
  el.classList.remove("is-editing")
  el.textContent = value || emptyText
  el.classList.toggle("is-empty", value === "" && emptyText !== "")
}

async function save(url: string, field: string, value: string): Promise<string | null> {
  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  if (!csrfToken) return null

  try {
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ paper: { [field]: value } }),
    })
    if (!response.ok) return null
    const payload = (await response.json()) as Record<string, string | null | undefined>
    return payload[field] ?? ""
  } catch {
    return null
  }
}

mountPaperFieldEditors()
